// src/components/tour-types/TourTypesTable.tsx
import React from "react";
import ColumnFilter from "@/components/shared/ui/ColumnFilter";
import Input from "@/components/shared/ui/Input";
import { FiEdit2, FiTrash2 } from "react-icons/fi";

export type TourTypeTableFilters = {
  name?: string;
  description?: string;
};

export type TourTypeRow = {
  id: string | number;
  name: string;
  description: string;
};

export default function TourTypesTable({
  rows: _rows, // unused (kept for parity)
  pageRows,
  serialStart,
  filters,
  setFilters,
  onEdit,
  onDelete,
}: {
  rows: TourTypeRow[];
  pageRows: TourTypeRow[];
  serialStart: number;
  filters: TourTypeTableFilters;
  setFilters: React.Dispatch<React.SetStateAction<TourTypeTableFilters>>;
  onEdit: (r: TourTypeRow) => void;
  onDelete: (r: TourTypeRow) => void;
}) {
  return (
    // Square bottom: the pagination bar below completes this card.
    <div className="overflow-hidden rounded-t-2xl border border-gray-200 bg-white">
      {/* Desktop table */}
      <div className="hidden overflow-x-auto overflow-visible md:block">
        <table className="min-w-full text-left text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50 text-gray-600">
            <tr className="border-b border-gray-200">
              <Th>S.No</Th>

              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Name</span>
                  <ColumnFilter label="Name">
                    <Input
                      placeholder="Contains…"
                      value={filters.name || ""}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, name: e.target.value }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Description</span>
                  <ColumnFilter label="Description">
                    <Input
                      placeholder="Contains…"
                      value={filters.description || ""}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          description: e.target.value,
                        }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              <Th className="text-right">Actions</Th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {pageRows.length === 0 ? (
              <tr>
                {/* 4 columns total */}
                <td
                  colSpan={4}
                  className="px-5 py-10 text-center text-sm text-gray-500"
                >
                  No tour types found.
                </td>
              </tr>
            ) : (
              pageRows.map((r, i) => (
                <tr key={r.id} className="hover:bg-gray-50/60">
                  <Td className="text-gray-700">{serialStart + i + 1}</Td>
                  <Td className="text-gray-900">{r.name}</Td>
                  <Td className="text-gray-700">
                    {r.description || <span className="text-gray-400">—</span>}
                  </Td>
                  <Td>
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                        onClick={() => onEdit(r)}
                        aria-label="Edit"
                        title="Edit"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                        onClick={() => onDelete(r)}
                        aria-label="Delete"
                        title="Delete"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile list */}
      <div className="divide-y divide-gray-200 md:hidden">
        {pageRows.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">
            No tour types found.
          </div>
        ) : (
          pageRows.map((r, i) => (
            <div key={r.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="pr-2">
                  <div className="text-sm font-medium text-gray-900">
                    {serialStart + i + 1}. {r.name}
                  </div>
                  {r.description && (
                    <div className="mt-0.5 text-xs text-gray-600">
                      {r.description}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                    onClick={() => onEdit(r)}
                    aria-label="Edit"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                    onClick={() => onDelete(r)}
                    aria-label="Delete"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Th({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      scope="col"
      className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wide ${className}`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={`px-5 py-3.5 ${className}`}>{children}</td>;
}
